<!-- eslint-disable vue/no-v-html -->
<template>
  <section class="message-set" :class="userClass">
    <div class="message">
      <div class="nickname" v-if="isShowName">
        {{ UserNameOnly(message.sender) }}
      </div>

      <div class="message-item">
        <div
          v-if="isUserMessage"
          class="text"
          v-html="safeHtml"
        ></div>

        <img
          v-else-if="isFileMessage && isImageFile && resolvedFileUrl"
          :src="resolvedFileUrl"
          class="chat-image"
          @click="openFileOrVideo"
        />

        <img
          v-else-if="isFileMessage && isVideoFile && videoThumbnail"
          :src="videoThumbnail"
          class="chat-thumbnail"
          @click="openFileOrVideo"
        />

        <video
          v-else-if="isFileMessage && isVideoFile && resolvedFileUrl && !videoThumbnail"
          class="chat-video"
          controls
          preload="metadata"
          @click="openFileOrVideo"
        >
          <source :src="resolvedFileUrl" :type="fileMimeType || undefined" />
          {{ t('dialog.chat.video.error') }}
        </video>

        <div
          v-else-if="isFileMessage && isPdfFile"
          class="chat-file-preview pdf-message"
        >
          <a :href="resolvedFileUrl" target="_blank" rel="noopener noreferrer">
            <font-awesome-icon icon="file-pdf" class="pdf-icon" />
            <span>{{ t('dialog.chat-message.pdf.download') }}</span>
          </a>
        </div>

        <div
          v-else-if="isFileMessage && isDocFile"
          class="chat-file-preview doc-message"
        >
          <a :href="resolvedFileUrl" target="_blank" rel="noopener noreferrer">
            <font-awesome-icon :icon="['fas', 'file-word']" class="doc-icon" />
            <span>{{ displayFileName }}</span>
          </a>
        </div>

        <div
          v-else-if="isFileMessage && isTextFile"
          class="chat-file-preview text-message"
        >
          <a :href="resolvedFileUrl" target="_blank" rel="noopener noreferrer">
            <font-awesome-icon icon="file-alt" class="text-icon" />
            <span>{{ displayFileName }}</span>
          </a>
        </div>

        <div
          v-else-if="isFileMessage"
          class="chat-file-preview generic-file-message"
          @click="openFileOrVideo"
        >
          📄 <span>{{ displayFileName }}</span>
        </div>

        <div v-else class="text">
          {{ message.message || displayFileName || 'Unsupported message' }}
        </div>

        <div class="time">{{ MessageCreatedAt(message.createdAt) }}</div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* eslint-disable vue/no-v-html */
import { computed, watch, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useVideoThumbnail } from '@/composables/useVideoThumbnail.js'
import {
  imageExtensions,
  videoExtensions,
  pdfExtensions,
  wordDocumentExtensions,
  otherDocumentExtensions,
  isMahle
} from '@/plugins/globals.js'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import DOMPurify from 'dompurify'

const props = defineProps({
  message: {
    type: Object,
    default: () => ({})
  },
  lastmessage: {
    type: Object,
    default: () => ({})
  },
  isHistory: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['image-clicked'])

const store = useStore()
const { t } = useI18n()
const { videoThumbnail, generateVideoThumbnail } = useVideoThumbnail()

const safeHtml = computed(() =>
  DOMPurify.sanitize(String(props.message?.message || ''))
)

const isShowName = computed(() => {
  const current = String(
    props.message?.sender?.userId ??
    props.message?.email ??
    ''
  ).toLowerCase()

  const previous = String(
    props.lastmessage?.sender?.userId ??
    props.lastmessage?.email ??
    ''
  ).toLowerCase()

  return current !== previous
})

const userClass = computed(() => {
  const chatAs = store.getters['auth/chatAs']
  const profile = store.getters['auth/userProfile']
  const me = String(chatAs?.email || profile?.email || '').toLowerCase()

  const senderId = String(
    props.message?.sender?.userId ??
    props.message?.email ??
    ''
  ).toLowerCase()

  return senderId && me && senderId === me ? 'user' : ''
})

const isFileMessage = computed(() => {
  const m = props.message
  if (!m) return false

  if (typeof m.isFileMessage === 'function') {
    try {
      return !!m.isFileMessage()
    } catch {}
  }

  return String(m.messageType || '').toLowerCase() === 'file'
})

const isUserMessage = computed(() => {
  const m = props.message
  if (!m) return false

  if (typeof m.isUserMessage === 'function') {
    try {
      return !!m.isUserMessage()
    } catch {}
  }

  return String(m.messageType || '').toLowerCase() === 'user'
})

const resolvedFileUrl = computed(() => {
  return String(
    props.message?.plainUrl ||
    props.message?.url ||
    props.message?.secureUrl ||
    props.message?.fileUrl ||
    props.message?.file?.url ||
    props.message?.file?.localUrl ||
    ''
  )
})

const fileName = computed(() => {
  return String(
    props.message?.name ||
    props.message?.fileName ||
    props.message?.filename ||
    props.message?.file?.name ||
    props.message?.file?.filename ||
    ''
  )
})

const displayFileName = computed(() => {
  return (
    fileName.value ||
    t('dialog.chat-message.file.type') ||
    'File'
  )
})

const fileMimeType = computed(() => {
  return String(
    props.message?.type ||
    props.message?.mimeType ||
    props.message?.contentType ||
    props.message?.file?.type ||
    props.message?.file?.mimeType ||
    props.message?.file?.contentType ||
    ''
  ).toLowerCase()
})

function getUrlPath(url) {
  try {
    const u = new URL(url, window.location.origin)
    return String(u.pathname || '').toLowerCase()
  } catch {
    return String(url || '')
      .split('?')[0]
      .split('#')[0]
      .toLowerCase()
  }
}

function getExtensionFromName(name) {
  const lower = String(name || '').toLowerCase().trim()
  const idx = lower.lastIndexOf('.')
  return idx >= 0 ? lower.substring(idx + 1) : ''
}

function checkFileType(extensions, contentTypePrefixes = []) {
  const mime = fileMimeType.value
  if (mime) {
    const matchedPrefix = contentTypePrefixes.some(prefix => mime.startsWith(prefix))
    if (matchedPrefix) return true
  }

  const extFromName = getExtensionFromName(fileName.value)
  if (extFromName && extensions.includes(extFromName)) {
    return true
  }

  const path = getUrlPath(resolvedFileUrl.value)
  if (path) {
    return extensions.some(ext => path.endsWith(`.${ext}`))
  }

  return false
}

const isImageFile = computed(() =>
  checkFileType(imageExtensions, ['image/'])
)

const isVideoFile = computed(() =>
  checkFileType(videoExtensions, ['video/'])
)

const isPdfFile = computed(() =>
  checkFileType(pdfExtensions, ['application/pdf'])
)

const isDocFile = computed(() =>
  checkFileType(wordDocumentExtensions, [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ])
)

const isTextFile = computed(() =>
  checkFileType(otherDocumentExtensions, ['text/'])
)

watch(
  () => ({
    messageId: props.message?.messageId ?? null,
    url: resolvedFileUrl.value,
    isVideo: isVideoFile.value
  }),
  ({ url, isVideo }) => {
    if (!isVideo || !url) return
    generateVideoThumbnail(url)
  },
  { immediate: true }
)

function UserNameOnly(sender) {
  const chatAs = store.getters['auth/chatAs']
  const profile = store.getters['auth/userProfile']
  const me = String(chatAs?.email || profile?.email || '').toLowerCase()
  const senderId = String(
    sender?.userId ??
    props.message?.email ??
    ''
  ).toLowerCase()

  if (senderId && senderId === me) {
    return t('dialog.chat-message.me') || 'Me'
  }

  const name =
    sender?.nickname ||
    sender?.userId ||
    props.message?.name ||
    props.message?.email ||
    ''

  if (isMahle()) {
    const atPos = name.lastIndexOf('@')
    const username = atPos > 0 ? name.substring(0, atPos) : name
    return username || name
  }

  return name
}

function MessageCreatedAt(createdAt) {
  if (!createdAt) return ''

  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return ''

  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function pad(val) {
  return +val < 10 ? '0' + val : String(val)
}

function openFileOrVideo() {
  if (!resolvedFileUrl.value) return
  emit('image-clicked', resolvedFileUrl.value)
}
</script>

<style scoped>
.custom-message-text {
  white-space: pre-wrap;
}

.chat-thumbnail {
  width: 160px;
  height: 160px;
  object-fit: cover;
  cursor: pointer;
  border-radius: 8px;
}

.chat-image {
  max-width: 200px;
  max-height: 200px;
  width: auto;
  height: auto;
  border-radius: 8px;
  object-fit: cover;
  margin-top: 5px;
  cursor: pointer;
}

.chat-video {
  max-width: 240px;
  max-height: 220px;
  border-radius: 8px;
  margin-top: 5px;
  cursor: pointer;
}

.chat-file-preview {
  color: #fff;
  background-color: #444;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-block;
}

.chat-file-preview.pdf-message {
  background-color: white;
  color: black;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.chat-file-preview.doc-message {
  background-color: white;
  color: black;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.chat-file-preview.text-message {
  background-color: white;
  color: black;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.generic-file-message {
  user-select: none;
}

.pdf-icon {
  color: #e43f27;
  font-size: 1.5em;
  margin-right: 8px;
}

.doc-icon {
  color: #2b579a;
  font-size: 1.5rem;
  margin-right: 5px;
  vertical-align: middle;
}

.text-icon {
  font-size: 1.2rem;
  margin-right: 6px;
  vertical-align: middle;
}

font-awesome-icon,
font-awesome-icon svg {
  background-color: transparent !important;
}
</style>
